import type { DemoData, DemoDataSummary, EnrichedPart, PartCategory } from "../domain/types";
import { DEFAULT_DEMO_GENERATOR_CONFIG, SOURCE_PRIORITY_RULES, TAXONOMY_MAPPINGS, type DemoGeneratorConfig } from "./generatorConfig";
import { generateCommercialActivity } from "./generateCommercialActivity";
import { generateManufacturers, generatePriceToleranceRules } from "./generateManufacturers";
import { generateParts } from "./generateParts";
import { generatePricing } from "./generatePricing";
import { SeededRandom } from "./seededRandom";

const emptyCategoryCounts = (): Record<PartCategory, number> => ({
    Semiconductor: 0,
    Capacitor: 0,
    Resistor: 0,
    Connector: 0,
});

const countBySource = (parts: EnrichedPart[]): Record<string, number> =>
    parts.reduce<Record<string, number>>((counts, part) => {
        counts[part.source] = (counts[part.source] ?? 0) + 1;
        return counts;
    }, {});

const countByCategory = (parts: EnrichedPart[]): Record<PartCategory, number> =>
    parts.reduce<Record<PartCategory, number>>((counts, part) => {
        counts[part.category] += 1;
        return counts;
    }, emptyCategoryCounts());

const buildSummary = (data: Omit<DemoData, "summary">): DemoDataSummary => ({
    partRows: data.parts.length,
    priceUpdateRows: data.priceUpdates.length,
    pricingHistoryRows: data.pricingHistory.length,
    opportunityRows: data.opportunities.length,
    purchaseOrderRows: data.purchaseOrders.length,
    catalogRows: data.catalog.length,
    toleranceRuleRows: data.priceToleranceRules.length,
    taxonomyMappingRows: data.taxonomyMappings.length,
    sourcePriorityRuleRows: data.sourcePriorityRules.length,
    mandatoryAttributeGaps: data.parts.filter((part) => part.hasMandatoryAttributeGap).length,
    lifecycleBlocks: data.parts.filter((part) => part.lifecycleStatus === "EOL" || part.lifecycleStatus === "NRND").length,
    complianceChanges: data.parts.filter((part) => part.hasComplianceChange).length,
    uomConflicts: data.parts.filter((part) => part.hasUomConflict).length,
    mpnFormatIssues: data.parts.filter((part) => part.hasMpnFormatIssue).length,
    lowConfidenceRows: data.parts.filter((part) => part.confidenceScore < 80).length,
    sourceConflicts: data.parts.filter((part) => part.hasSourceConflict).length,
    sourceCountByName: countBySource(data.parts),
    categoryCountByName: countByCategory(data.parts),
});

export const generateDemoData = (overrides: Partial<DemoGeneratorConfig> = {}): DemoData => {
    const config: DemoGeneratorConfig = {
        ...DEFAULT_DEMO_GENERATOR_CONFIG,
        ...overrides,
    };
    const rng = new SeededRandom(config.seed);
    const manufacturers = generateManufacturers();
    const { parts, catalog } = generateParts(config, rng, manufacturers);
    const { pricingHistory, priceUpdates } = generatePricing(config, rng, parts, manufacturers);
    const { opportunities, purchaseOrders } = generateCommercialActivity(config, rng, parts);
    const dataWithoutSummary = {
        seed: config.seed,
        manufacturers,
        parts,
        catalog,
        priceUpdates,
        pricingHistory,
        priceToleranceRules: generatePriceToleranceRules(manufacturers),
        taxonomyMappings: TAXONOMY_MAPPINGS.map((mapping) => ({ ...mapping })),
        sourcePriorityRules: SOURCE_PRIORITY_RULES.map((rule) => ({ ...rule })),
        opportunities,
        purchaseOrders,
    };

    return {
        ...dataWithoutSummary,
        summary: buildSummary(dataWithoutSummary),
    };
};

export const DATA_SOURCE_LABELS: Array<{ key: keyof DemoData; label: string }> = [
    { key: "parts", label: "enriched_parts" },
    { key: "priceUpdates", label: "supplier_price_updates" },
    { key: "pricingHistory", label: "pricing_history_90d" },
    { key: "priceToleranceRules", label: "price_tolerance_rules" },
    { key: "taxonomyMappings", label: "taxonomy_product_mapping" },
    { key: "sourcePriorityRules", label: "manufacturer_source_priority" },
    { key: "catalog", label: "snowflake_master_catalog" },
    { key: "opportunities", label: "open_crm_opportunities" },
    { key: "purchaseOrders", label: "open_erp_purchase_orders" },
];

